// src/pages/providers/3_analytics/ProviderAnalytics.js
import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import Layout from '../../../components/Layout';
import { GlobalStateContext } from '../../../context/GlobalStateContext';
import { functions } from '../../../firebase';
import { httpsCallable } from 'firebase/functions';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  ChartBarIcon,
  UsersIcon,
  CheckCircleIcon,
  ClipboardCheckIcon,
  DownloadIcon
} from '@heroicons/react/solid';
// `xlsx` is a large dependency only needed when a user actually downloads an
// export. It is lazy-loaded via dynamic import() inside each download handler
// so it is excluded from the initial page bundle (faster first paint).
// Single source of truth for contract / assessment-type labels per org
// (must stay in sync with the Python analytics buckets).
import { ASSESSMENT_TYPES_BY_ORG } from '../../../constants/assessmentTypesByOrg';
// Referral traffic-source labels/options (single FE source of truth). Only the
// WLC referral panel uses these; the backend emits raw source keys under
// referrals.bySource and leaves labelling to the frontend.
import {
  REFERRAL_SOURCE_ALL,
  REFERRAL_SOURCE_KEYS,
  REFERRAL_SOURCE_OPTIONS,
  referralSourceLabel,
} from '../../../constants/referralSources';
import {
  buildDemographicsAllTimeSheet,
  buildDemographicsLast6MonthsSheet,
  listDemographicDimensionKeys,
  buildOutcomesLast6MonthsSheet,
  buildOutcomesAllTimeSheets,
  resolveOutcomesAllTimeSlice,
  resolveOutcomesByAssessmentType,
  buildWlcReferralSheets,
} from './analyticsExportBuilders';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

// Static chart options extracted at module level to avoid re-allocating the same
// config object on every render. Since these options contain no dynamic data (just
// fixed layout/display settings), they are referentially stable and safe to share.
const CHART_OPTIONS_STATIC = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false }, datalabels: { display: false } },
  scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } },
};

// Defined at module level (outside ProviderAnalytics) so React doesn't recreate the
// component type on every parent render. Inner-component definitions cause the entire
// subtree to unmount/remount each render cycle, resetting internal state and hurting perf.
const LoadingSpinner = () => (
  <div className="w-full h-24 flex items-center justify-center">
    <div className="inline-flex items-center px-4 py-2 bg-violet-100 text-slate-800 rounded-lg">
      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-slate-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <span className="font-semibold">Loading analytics...</span>
    </div>
  </div>
);

/**
 * The one-line notice shown when the backend served a cached payload that is
 * past its refresh time (`data.stale === true`).
 *
 * Why this exists: the analytics are pre-computed on a schedule and cached.
 * If the scheduled refresh misses a cycle, the backend serves the previous
 * figures straight away (marked stale) rather than making the clinician wait
 * minutes for a recompute. The honest thing is to say so, quietly: the numbers
 * on screen are real, they are just from a little earlier than usual.
 *
 * Wording: "They refresh automatically" rather than "a refresh is in
 * progress". Serving a stale payload does not start anything; the next
 * scheduled run replaces it. The second phrasing would be a claim the
 * backend cannot always back (if the scheduler itself is the thing failing),
 * and the design rules ask that we never imply more than the system does.
 *
 * `generatedAt` is the backend's ISO timestamp for when the figures were
 * computed. We show only the time of day, in the clinician's local time,
 * because the notice is about recency within the day; an unparseable value
 * drops the time rather than showing "Invalid Date".
 *
 * Exported for tests.
 */
export function StaleDataNotice({ generatedAt }) {
  const parsed = generatedAt ? new Date(generatedAt) : null;
  const hasTime = parsed instanceof Date && !Number.isNaN(parsed.getTime());
  const timeText = hasTime
    ? parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;
  return (
    <p role="status" className="text-sm text-gray-600 mb-4" data-testid="stale-data-notice">
      {timeText
        ? `Figures last updated at ${timeText}. They refresh automatically.`
        : 'Figures are from an earlier update. They refresh automatically.'}
    </p>
  );
}

function ProviderAnalytics() {
  const { userData } = useContext(GlobalStateContext);
  const [isLoading, setIsLoading] = useState(true);
  const [period, setPeriod] = useState('week'); // day|week|month
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // `timedOut` is a distinct, non-error state for the specific case where the
  // callable hit its 300s deadline. That almost always means the backend was
  // mid-compute on a cold cache, which usually finishes and populates the
  // cache shortly after — so we show a "still preparing, try again" message
  // with a manual retry rather than a hard error. `retryNonce` re-runs the
  // fetch effect when the user clicks Retry.
  const [timedOut, setTimedOut] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [demographicsModalOpen, setDemographicsModalOpen] = useState(false);
  const [selectedDemographic, setSelectedDemographic] = useState(null);

  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const downloadMenuRef = useRef(null);
  // Outcomes download menu mirrors the demographics one so each panel can
  // open/close independently without fighting over a single dropdown.
  const [outcomesDownloadMenuOpen, setOutcomesDownloadMenuOpen] = useState(false);
  const outcomesDownloadMenuRef = useRef(null);
  // WLC referral panel download menu (independent of the two above).
  const [referralDownloadMenuOpen, setReferralDownloadMenuOpen] = useState(false);
  const referralDownloadMenuRef = useRef(null);

  const [viewMode, setViewMode] = useState('assessments'); // 'assessments' | 'referrals'
  const hasReferrals = useMemo(() => (data?.referrals?.enabled === true), [data]);

  // WLC referral traffic-source filter ('all' | 'main' | 'nhs-adhd-patient-dot-info').
  // Independent of the assessments Contract Type filter and only surfaced in the
  // WLC referral view. Re-scopes the referral cards, charts, conversion-time
  // analysis and export to the selected source.
  const [referralSourceFilter, setReferralSourceFilter] = useState(REFERRAL_SOURCE_ALL);

  // Contract Type filters — options come from the shared org map. Orgs not
  // listed there get an empty list, which hides the filter entirely.
  // Demographics and Assessment Outcomes each keep their OWN filter state so a
  // clinician can, for example, view NHS - Kent demographics while leaving
  // outcomes on "All". They must never share a single state.
  const [demographicsTypeFilter, setDemographicsTypeFilter] = useState('all');
  const [outcomesTypeFilter, setOutcomesTypeFilter] = useState('all');
  const assessmentTypeOptions = ASSESSMENT_TYPES_BY_ORG[userData?.organisation] || [];
  const showAssessmentTypeFilter = assessmentTypeOptions.length > 0;

  // Fetch analytics data with retry logic and unmount-safety.
  // A `cancelled` flag prevents calling setState after the component unmounts
  // (e.g. user navigates away mid-request). httpsCallable doesn't support
  // AbortController, so a simple boolean flag is the cleanest approach.
  useEffect(() => {
    const cancelled = { current: false };

    const fetchAnalytics = async () => {
      if (!userData?.organisation) {
        if (!cancelled.current) {
          setData(null);
          setIsLoading(false);
        }
        return;
      }
      if (!cancelled.current) {
        setIsLoading(true);
        setError(null);
        setTimedOut(false);
      }

      // Retry up to MAX_RETRIES times with exponential backoff to handle
      // transient Cloud Function / network failures transparently.
      const MAX_RETRIES = 2;
      const BASE_DELAY_MS = 2000;
      let lastError = null;

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        if (cancelled.current) return;
        try {
          const getProviderAnalytics = httpsCallable(functions, 'get_provider_analytics', { timeout: 300000 });
          const res = await getProviderAnalytics({});
          if (cancelled.current) return;
          setData(res?.data || null);
          setIsLoading(false);
          return;
        } catch (e) {
          lastError = e;
          const code = e?.code || '';
          console.warn(`[ProviderAnalytics] attempt ${attempt + 1}/${MAX_RETRIES + 1} failed (code=${code}):`, e?.message || e);

          // Deadline exceeded: the 300s call timed out. The backend cold-cache
          // compute is very likely still running and will populate the cache
          // shortly, so firing another 300s call would only add load. Stop and
          // surface the "still preparing" state with a manual retry instead.
          if (code === 'functions/deadline-exceeded') {
            if (cancelled.current) return;
            setTimedOut(true);
            setData(null);
            setIsLoading(false);
            return;
          }

          // Only retry errors that a retry could actually fix: transient
          // backend/network failures. Permanent errors (permission-denied,
          // unauthenticated, invalid-argument, etc.) are surfaced immediately.
          // Firebase network failures often arrive with no code or as
          // internal/unavailable/unknown.
          const isTransient = (
            code === '' ||
            code === 'functions/internal' ||
            code === 'functions/unavailable' ||
            code === 'functions/unknown'
          );
          if (isTransient && attempt < MAX_RETRIES) {
            await new Promise((r) => setTimeout(r, BASE_DELAY_MS * Math.pow(2, attempt)));
            continue;
          }
          break;
        }
      }

      if (cancelled.current) return;
      console.error('Error fetching provider analytics after retries:', lastError);
      setError('Failed to load analytics');
      setData(null);
      setIsLoading(false);
    };

    fetchAnalytics();

    return () => {
      cancelled.current = true;
    };
  }, [userData?.organisation, retryNonce]);

  // Manual retry: clears the timeout/error state and bumps the nonce so the
  // fetch effect re-runs. Used by both the "still preparing" and error views.
  const handleRetryFetch = useCallback(() => {
    setTimedOut(false);
    setError(null);
    setRetryNonce((n) => n + 1);
  }, []);

  // Close demographics / outcomes / referral download dropdowns on outside click
  useEffect(() => {
    if (!downloadMenuOpen && !outcomesDownloadMenuOpen && !referralDownloadMenuOpen) return;
    const onDocClick = (e) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target)) {
        setDownloadMenuOpen(false);
      }
      if (outcomesDownloadMenuRef.current && !outcomesDownloadMenuRef.current.contains(e.target)) {
        setOutcomesDownloadMenuOpen(false);
      }
      if (referralDownloadMenuRef.current && !referralDownloadMenuRef.current.contains(e.target)) {
        setReferralDownloadMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [downloadMenuOpen, outcomesDownloadMenuOpen, referralDownloadMenuOpen]);

  // Handle escape key to close modals
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        if (feedbackModalOpen) setFeedbackModalOpen(false);
        if (demographicsModalOpen) {
          setDemographicsModalOpen(false);
          setSelectedDemographic(null);
        }
      }
    };
    if (feedbackModalOpen || demographicsModalOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [feedbackModalOpen, demographicsModalOpen]);

  const analyticsSummary = data?.assessments?.summary || { totalAdded: 0, assessmentsStarted: 0, assessmentRate: '0%', avgTimeToStart: 'N/A' };
  const chartsAll = data?.assessments?.charts || {};
  const chart = chartsAll?.[period] || data?.assessments?.chart || { labels: [], patientsAdded: [], assessmentsStarted: [] };
  const forms = data?.forms || { avgCompletion: 0, avgTimePerForm: 'N/A', avgTimeAllForms: 'N/A', patientExperience: '-', startedPatientsCount: 0, completedAtLeastOneFormCount: 0, completedAllFormsCount: 0 };
  // Demographics slice: select by the Demographics contract filter (or 'all').
  // A specific contract with no bucket must resolve to an EMPTY slice, never
  // silently fall back to the org-wide 'all' numbers (that would mislabel
  // all-org data as belonging to one contract). Only the aggregate view falls
  // back to 'all' (also covers legacy payloads that only have 'all').
  const demographicsSlice = React.useMemo(() => {
    const byType = data?.demographics?.byAssessmentType || {};
    const selectedType = showAssessmentTypeFilter ? demographicsTypeFilter : 'all';
    if (byType[selectedType]) return byType[selectedType];
    if (selectedType === 'all' && byType.all) return byType.all;
    return { allTime: {}, last6Months: { months: [], dimensions: {} } };
  }, [data?.demographics?.byAssessmentType, showAssessmentTypeFilter, demographicsTypeFilter]);
  const demographicsAllTime = React.useMemo(() => (demographicsSlice?.allTime || {}), [demographicsSlice]);
  const demographicsLast6 = React.useMemo(() => (demographicsSlice?.last6Months || { months: [], dimensions: {} }), [demographicsSlice]);
  // Only dimensions with at least one real response are shown. Filtering here
  // (rather than inside the grid) means the panel can distinguish "has data"
  // from "empty" and render a proper empty state / disable Download.
  const demographicsDimensions = React.useMemo(
    () => Object.entries(demographicsAllTime).filter(([, dim]) => (dim?.total_responses || 0) > 0),
    [demographicsAllTime]
  );
  const demographicsHasData = demographicsDimensions.length > 0;
  const feedback = data?.feedback || { taskRatingCounts: {}, platformRatingCounts: {}, avgTaskRating: null, avgPlatformRating: null, list: [] };
  // Assessment Outcomes panel binds to the allTime slice for the selected
  // contract (or "all"). resolve* helpers also bridge legacy flat payloads
  // so cached v7 responses still render during rollout.
  const outcomes = useMemo(
    () => resolveOutcomesAllTimeSlice(data?.outcomes, outcomesTypeFilter, showAssessmentTypeFilter),
    [data?.outcomes, outcomesTypeFilter, showAssessmentTypeFilter]
  );
  const outcomesByAssessmentType = useMemo(
    () => resolveOutcomesByAssessmentType(data?.outcomes),
    [data?.outcomes]
  );
  // Whether this org has ANY diagnostic outcomes at all (aggregate view). Used
  // to decide if the Outcomes panel shows at all — independent of the contract
  // filter, so selecting a zero-data contract shows an empty state rather than
  // making the whole panel vanish.
  const hasAnyOutcomes = (outcomesByAssessmentType?.all?.allTime?.totalDecided || 0) > 0;
  // Whether the CURRENTLY SELECTED outcomes slice has data (drives empty state
  // + Download disabling within the panel).
  const outcomesHasData = (outcomes?.totalDecided || 0) > 0;
  const referrals = data?.referrals || { enabled: false, metrics: {}, charts: {} };
  // Phoenix uses { labels, referrals } per period; WLC uses { labels, uniqueReferrals, convertedToAssessment }.
  // Both chart shapes are consumed directly in their respective sections below.
  const referralsChart = referrals?.charts?.[period] || { labels: [], referrals: [], uniqueReferrals: [], convertedToAssessment: [] };

  // WLC referral analytics are additionally split by traffic source under
  // referrals.bySource. Resolve the slice for the selected source, falling back
  // to the top-level aggregate for a legacy (pre-bySource, v8) payload so the
  // panel keeps rendering during cache rollout. Phoenix has no bySource and is
  // unaffected — it keeps using `referralsChart` above.
  const referralsBySource = referrals?.bySource || null;
  const wlcReferralSlice = React.useMemo(() => {
    if (referralsBySource && referralsBySource[referralSourceFilter]) {
      return referralsBySource[referralSourceFilter];
    }
    return {
      metrics: referrals?.metrics || {},
      charts: referrals?.charts || {},
      conversionStats: referrals?.conversionStats || null,
    };
  }, [referralsBySource, referralSourceFilter, referrals]);
  const wlcReferralMetrics = wlcReferralSlice?.metrics || {};
  const wlcReferralConversionStats = wlcReferralSlice?.conversionStats || null;
  const wlcReferralsChart = wlcReferralSlice?.charts?.[period]
    || { labels: [], uniqueReferrals: [], convertedToAssessment: [] };

  // Memoised chart data objects — without useMemo these would be new object references
  // every render, forcing Chart.js to fully re-draw even when the underlying data hasn't
  // changed. Dependencies are the slices of state that actually feed into the datasets.
  const patientsAddedChartData = useMemo(() => ({
    labels: chart.labels,
    datasets: [{
      label: 'Patients Added',
      data: chart.patientsAdded,
      backgroundColor: 'rgba(139, 92, 246, 0.8)',
      borderColor: 'rgba(139, 92, 246, 1)',
      borderWidth: 1,
    }],
  }), [chart.labels, chart.patientsAdded]);

  const assessmentsStartedChartData = useMemo(() => ({
    labels: chart.labels,
    datasets: [{
      label: 'Assessments Started',
      data: chart.assessmentsStarted,
      backgroundColor: 'rgba(139, 92, 246, 0.8)',
      borderColor: 'rgba(139, 92, 246, 1)',
      borderWidth: 1,
    }],
  }), [chart.labels, chart.assessmentsStarted]);

  const referralsPhoenixChartData = useMemo(() => ({
    labels: referralsChart.labels,
    datasets: [{
      label: 'Referrals',
      data: referralsChart.referrals,
      backgroundColor: 'rgba(139, 92, 246, 0.8)',
      borderColor: 'rgba(139, 92, 246, 1)',
      borderWidth: 1,
    }],
  }), [referralsChart.labels, referralsChart.referrals]);

  const referralsWlcUniqueChartData = useMemo(() => ({
    labels: wlcReferralsChart.labels,
    datasets: [{
      label: 'Referrals',
      data: wlcReferralsChart.uniqueReferrals || [],
      backgroundColor: 'rgba(139, 92, 246, 0.8)',
      borderColor: 'rgba(139, 92, 246, 1)',
      borderWidth: 1,
    }],
  }), [wlcReferralsChart.labels, wlcReferralsChart.uniqueReferrals]);

  const referralsWlcConvertedChartData = useMemo(() => ({
    labels: wlcReferralsChart.labels,
    datasets: [{
      label: 'Converted',
      data: wlcReferralsChart.convertedToAssessment || [],
      backgroundColor: 'rgba(16, 185, 129, 0.8)',
      borderColor: 'rgba(16, 185, 129, 1)',
      borderWidth: 1,
    }],
  }), [wlcReferralsChart.labels, wlcReferralsChart.convertedToAssessment]);

  // Data freshness UI
  const freshnessText = React.useMemo(() => {
    if (!data?.generatedAt) return null;
    try {
      const dt = new Date(data.generatedAt);
      return `Last updated: ${dt.toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`;
    } catch {
      return null;
    }
  }, [data?.generatedAt]);

  // Format feedback timestamp and ensure newest-first sort (defensive against backend order)
  const formatFeedbackDate = useCallback((iso) => {
    if (!iso) return 'N/A';
    try {
      const d = new Date(iso);
      // e.g., "1:05pm on 13 Nov 2025"
      let time = d.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true });
      // Normalize to "h:mmam/pm" (remove space before am/pm and lower-case)
      time = time.replace(/\s?(AM|PM)$/i, (m) => m.trim().toLowerCase());
      const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      return `${time} on ${date}`;
    } catch {
      return 'N/A';
    }
  }, []);

  const sortedFeedbackList = React.useMemo(() => {
    const list = Array.isArray(feedback?.list) ? feedback.list : [];
    return [...list].sort((a, b) => {
      const da = a?.submittedAt ? new Date(a.submittedAt).getTime() : 0;
      const db = b?.submittedAt ? new Date(b.submittedAt).getTime() : 0;
      return db - da; // newest first
    });
  }, [feedback?.list]);

  // Feedback modal open
  const openFeedbackModal = () => setFeedbackModalOpen(true);

  // Demographics modal handlers
  const openDemographicsModal = (key, data) => {
    setSelectedDemographic({ key, data });
    setDemographicsModalOpen(true);
  };
  const closeDemographicsModal = () => {
    setDemographicsModalOpen(false);
    setSelectedDemographic(null);
  };

  // Shared Excel header styling (light-blue fill + bold). Column letter
  // encoding supports >26 columns (last-6-months month grids). Takes the
  // lazily-imported XLSX module as an argument since it is no longer a
  // module-level import.
  const styleWorkbookSheet = (XLSX, ws, rows) => {
    const subheaderFillColor = 'D9E1F2';
    if (!rows?.length) return;
    const colCount = rows[0].length;
    ws['!cols'] = Array.from({ length: colCount }, (_, i) => ({
      wch: i === 0 ? 35 : (i === 1 && colCount > 3 ? 28 : 16),
    }));
    for (let c = 0; c < colCount; c++) {
      const cell = `${XLSX.utils.encode_col(c)}1`;
      if (ws[cell]) {
        ws[cell].s = {
          font: { bold: true, color: { rgb: '000000' } },
          fill: { fgColor: { rgb: subheaderFillColor } },
          alignment: { horizontal: 'center', vertical: 'center' },
        };
      }
    }
    const lastRowIdx = rows.length;
    for (let c = 0; c < colCount; c++) {
      const addr = `${XLSX.utils.encode_col(c)}${lastRowIdx}`;
      if (ws[addr]) {
        ws[addr].s = {
          font: { bold: true },
          alignment: { horizontal: c === 0 ? 'left' : 'right' },
        };
      }
    }
    for (let r = 2; r < lastRowIdx; r++) {
      for (let c = 0; c < colCount; c++) {
        const addr = `${XLSX.utils.encode_col(c)}${r}`;
        if (ws[addr]) {
          ws[addr].s = { alignment: { horizontal: c === 0 ? 'left' : 'right' } };
        }
      }
    }
  };

  // Filename helper: append a contract slug only when a specific contract is
  // selected (e.g. `_NHS_Kent`), so "All" downloads stay org-wide. Takes the
  // panel's own filter value because Demographics and Outcomes now filter
  // independently and each export must reflect its own selection.
  const buildContractFilenameSuffix = useCallback((filterValue) => {
    if (!showAssessmentTypeFilter || filterValue === 'all') return '';
    return `_${filterValue.replace(/\s+/g, '_').replace(/-/g, '_')}`;
  }, [showAssessmentTypeFilter]);

  // Downloads - All-time demographics (uses byAssessmentType so "All" can
  // break out one row per contract when the org has contract options).
  const handleDownloadDemographicsAllTime = useCallback(async () => {
    const byAssessmentType = data?.demographics?.byAssessmentType;
    const dimensionKeys = listDemographicDimensionKeys(byAssessmentType, demographicsTypeFilter);
    if (!byAssessmentType || dimensionKeys.length === 0) {
      alert('No demographics data available to download.');
      return;
    }
    setIsDownloading(true);
    try {
      await new Promise((r) => setTimeout(r, 100));
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();
      dimensionKeys.forEach((dimensionKey) => {
        const sheet = buildDemographicsAllTimeSheet({
          byAssessmentType,
          dimensionKey,
          contractOptions: assessmentTypeOptions,
          assessmentTypeFilter: demographicsTypeFilter,
        });
        if (!sheet) return;
        const ws = XLSX.utils.aoa_to_sheet(sheet.rows);
        styleWorkbookSheet(XLSX, ws, sheet.rows);
        XLSX.utils.book_append_sheet(wb, ws, sheet.sheetName);
      });
      if (wb.SheetNames.length === 0) {
        const ws = XLSX.utils.aoa_to_sheet([['No demographic data available']]);
        ws['A1'].s = { font: { bold: true } };
        XLSX.utils.book_append_sheet(wb, ws, 'No Data');
      }
      const orgName = (userData?.organisation || 'Organisation').replace(/\s+/g, '_');
      const contractSuffix = buildContractFilenameSuffix(demographicsTypeFilter);
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      XLSX.writeFile(wb, `Demographics_Analytics_${orgName}${contractSuffix}_${ts}.xlsx`);
    } catch (e) {
      console.error('Error generating demographics Excel:', e);
      alert('Error generating Excel file. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  }, [
    data?.demographics?.byAssessmentType,
    assessmentTypeOptions,
    demographicsTypeFilter,
    userData?.organisation,
    buildContractFilenameSuffix,
  ]);

  // Downloads - Last 6 months demographics (month = patient createdAt month).
  const handleDownloadDemographicsLast6Months = useCallback(async () => {
    const byAssessmentType = data?.demographics?.byAssessmentType;
    const dimensionKeys = listDemographicDimensionKeys(byAssessmentType, demographicsTypeFilter);
    if (!byAssessmentType || dimensionKeys.length === 0) {
      alert('No demographics data available to download.');
      return;
    }
    setIsDownloading(true);
    try {
      await new Promise((r) => setTimeout(r, 100));
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();
      dimensionKeys.forEach((dimensionKey) => {
        const sheet = buildDemographicsLast6MonthsSheet({
          byAssessmentType,
          dimensionKey,
          contractOptions: assessmentTypeOptions,
          assessmentTypeFilter: demographicsTypeFilter,
        });
        if (!sheet) return;
        const ws = XLSX.utils.aoa_to_sheet(sheet.rows);
        styleWorkbookSheet(XLSX, ws, sheet.rows);
        XLSX.utils.book_append_sheet(wb, ws, sheet.sheetName);
      });
      if (wb.SheetNames.length === 0) {
        const ws = XLSX.utils.aoa_to_sheet([['No demographic data available for the last 6 months']]);
        ws['A1'].s = { font: { bold: true } };
        XLSX.utils.book_append_sheet(wb, ws, 'No Data');
      }
      const orgName = (userData?.organisation || 'Organisation').replace(/\s+/g, '_');
      const contractSuffix = buildContractFilenameSuffix(demographicsTypeFilter);
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      XLSX.writeFile(wb, `Demographics_Analytics_Last6Months_${orgName}${contractSuffix}_${ts}.xlsx`);
    } catch (e) {
      console.error('Error generating last 6 months demographics Excel:', e);
      alert('Error generating Excel file. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  }, [
    data?.demographics?.byAssessmentType,
    assessmentTypeOptions,
    demographicsTypeFilter,
    userData?.organisation,
    buildContractFilenameSuffix,
  ]);

  // Outcomes downloads are available for every org (not only contract orgs).
  // When the org has contracts and filter is "All", sheets break out by contract.
  const handleDownloadOutcomesAllTime = useCallback(async () => {
    if (!outcomesByAssessmentType) {
      alert('No outcomes data available to download.');
      return;
    }
    setIsDownloading(true);
    try {
      await new Promise((r) => setTimeout(r, 100));
      const XLSX = await import('xlsx');
      const sheets = buildOutcomesAllTimeSheets({
        byAssessmentType: outcomesByAssessmentType,
        contractOptions: assessmentTypeOptions,
        assessmentTypeFilter: outcomesTypeFilter,
      });
      const wb = XLSX.utils.book_new();
      sheets.forEach((sheet) => {
        const ws = XLSX.utils.aoa_to_sheet(sheet.rows);
        styleWorkbookSheet(XLSX, ws, sheet.rows);
        XLSX.utils.book_append_sheet(wb, ws, sheet.sheetName);
      });
      if (wb.SheetNames.length === 0) {
        const ws = XLSX.utils.aoa_to_sheet([['No outcomes data available']]);
        ws['A1'].s = { font: { bold: true } };
        XLSX.utils.book_append_sheet(wb, ws, 'No Data');
      }
      const orgName = (userData?.organisation || 'Organisation').replace(/\s+/g, '_');
      const contractSuffix = buildContractFilenameSuffix(outcomesTypeFilter);
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      XLSX.writeFile(wb, `Assessment_Outcomes_${orgName}${contractSuffix}_${ts}.xlsx`);
    } catch (e) {
      console.error('Error generating outcomes Excel:', e);
      alert('Error generating Excel file. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  }, [
    outcomesByAssessmentType,
    assessmentTypeOptions,
    outcomesTypeFilter,
    userData?.organisation,
    buildContractFilenameSuffix,
  ]);

  const handleDownloadOutcomesLast6Months = useCallback(async () => {
    if (!outcomesByAssessmentType) {
      alert('No outcomes data available to download.');
      return;
    }
    setIsDownloading(true);
    try {
      await new Promise((r) => setTimeout(r, 100));
      const XLSX = await import('xlsx');
      const sheet = buildOutcomesLast6MonthsSheet({
        byAssessmentType: outcomesByAssessmentType,
        contractOptions: assessmentTypeOptions,
        assessmentTypeFilter: outcomesTypeFilter,
      });
      const wb = XLSX.utils.book_new();
      if (sheet) {
        const ws = XLSX.utils.aoa_to_sheet(sheet.rows);
        styleWorkbookSheet(XLSX, ws, sheet.rows);
        XLSX.utils.book_append_sheet(wb, ws, sheet.sheetName);
      } else {
        const ws = XLSX.utils.aoa_to_sheet([['No outcomes data available for the last 6 months']]);
        ws['A1'].s = { font: { bold: true } };
        XLSX.utils.book_append_sheet(wb, ws, 'No Data');
      }
      const orgName = (userData?.organisation || 'Organisation').replace(/\s+/g, '_');
      const contractSuffix = buildContractFilenameSuffix(outcomesTypeFilter);
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      XLSX.writeFile(wb, `Assessment_Outcomes_Last6Months_${orgName}${contractSuffix}_${ts}.xlsx`);
    } catch (e) {
      console.error('Error generating last 6 months outcomes Excel:', e);
      alert('Error generating Excel file. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  }, [
    outcomesByAssessmentType,
    assessmentTypeOptions,
    outcomesTypeFilter,
    userData?.organisation,
    buildContractFilenameSuffix,
  ]);

  // Downloads - WLC referrals & conversions by day/week/month (all-time).
  // Builds one workbook: Summary + By Day/Week/Month sheets. Honours the source
  // filter (a specific source shows 3 columns; "All" breaks each date down by
  // source). Uses the all-time exportSeries, so it is unaffected by the capped
  // on-screen day chart.
  const handleDownloadWlcReferrals = useCallback(async () => {
    const bySource = referrals?.bySource;
    if (!bySource || !bySource[REFERRAL_SOURCE_ALL]) {
      alert('No referral data available to download.');
      return;
    }
    setIsDownloading(true);
    try {
      await new Promise((r) => setTimeout(r, 100));
      const XLSX = await import('xlsx');
      const sheets = buildWlcReferralSheets({ bySource, sourceFilter: referralSourceFilter });
      const wb = XLSX.utils.book_new();
      if (sheets.length > 0) {
        sheets.forEach((sheet) => {
          const ws = XLSX.utils.aoa_to_sheet(sheet.rows);
          styleWorkbookSheet(XLSX, ws, sheet.rows);
          XLSX.utils.book_append_sheet(wb, ws, sheet.sheetName);
        });
      } else {
        const ws = XLSX.utils.aoa_to_sheet([['No referral data available']]);
        ws['A1'].s = { font: { bold: true } };
        XLSX.utils.book_append_sheet(wb, ws, 'No Data');
      }
      const orgName = (userData?.organisation || 'Organisation').replace(/\s+/g, '_');
      // Append a source slug only when a specific source is selected.
      const sourceSuffix = referralSourceFilter && referralSourceFilter !== REFERRAL_SOURCE_ALL
        ? `_${referralSourceLabel(referralSourceFilter).replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '')}`
        : '';
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      XLSX.writeFile(wb, `WLC_Referrals_${orgName}${sourceSuffix}_${ts}.xlsx`);
    } catch (e) {
      console.error('Error generating referrals Excel:', e);
      alert('Error generating Excel file. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  }, [
    referrals?.bySource,
    referralSourceFilter,
    userData?.organisation,
  ]);

  const getEmojiForRating = (rating) => {
    switch (rating) {
      case 1: return "😞";
      case 2: return "🙁";
      case 3: return "😐";
      case 4: return "🙂";
      case 5: return "😊";
      default: return "😐";
    }
  };
  const getTaskRatingLabel = (rating) => {
    switch (rating) {
      case 1: return "Overwhelming";
      case 2: return "Quite challenging";
      case 3: return "Manageable";
      case 4: return "Quite straightforward";
      case 5: return "Straightforward";
      default: return "";
    }
  };
  const getPlatformRatingLabel = (rating) => {
    switch (rating) {
      case 1: return "Difficult to use";
      case 2: return "Somewhat difficult";
      case 3: return "Okay";
      case 4: return "Quite smooth";
      case 5: return "Very smooth";
      default: return "";
    }
  };

  const sortedDemographicEntries = (display) => {
    const entries = Object.entries(display.responses || {});
    return entries.sort((a, b) => {
      const getOrder = (resp) => {
        if (resp === 'Not yet seen') return 3;
        if (resp === 'Seen but unanswered') return 2;
        return 1;
      };
      const oa = getOrder(a[0]);
      const ob = getOrder(b[0]);
      if (oa !== ob) return oa - ob;
      return b[1] - a[1];
    });
  };

  return (
    <Layout activePage="analytics">
      <div className="flex-grow px-4 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-3 items-center mb-6">
            <div></div>
            <h1 className="text-2xl font-bold text-gray-900 text-center justify-self-center">Analytics</h1>
            <div className="justify-self-end">
              {hasReferrals && (
                <div className="inline-flex items-center bg-gray-100 rounded-lg p-1 border border-gray-200">
                  <button
                    className={`px-3 py-1 text-sm rounded-md ${viewMode === 'assessments' ? 'bg-white border border-gray-300 shadow-sm text-gray-900' : 'text-gray-600'}`}
                    onClick={() => setViewMode('assessments')}
                  >
                    Assessments
                  </button>
                  <button
                    className={`ml-1 px-3 py-1 text-sm rounded-md ${viewMode === 'referrals' ? 'bg-white border border-gray-300 shadow-sm text-gray-900' : 'text-gray-600'}`}
                    onClick={() => setViewMode('referrals')}
                  >
                    Referrals
                  </button>
                </div>
              )}
            </div>
          </div>

          {isLoading ? (
            <LoadingSpinner />
          ) : timedOut ? (
            <div className="text-center py-12">
              <p className="text-gray-800 font-medium">Your analytics are still being prepared.</p>
              <p className="text-sm text-gray-500 mt-1">
                This can take a moment for larger organisations. Please try again in a minute.
              </p>
              <button
                type="button"
                onClick={handleRetryFetch}
                className="mt-4 inline-flex items-center px-4 py-2 bg-violet-600 text-white text-sm font-semibold rounded-lg hover:bg-violet-700 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-600">{error}</div>
              <button
                type="button"
                onClick={handleRetryFetch}
                className="mt-4 inline-flex items-center px-4 py-2 bg-violet-600 text-white text-sm font-semibold rounded-lg hover:bg-violet-700 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              {/* Served from a cache past its refresh time: say so, once,
                  above every panel. The figures below are still shown. */}
              {data?.stale === true && <StaleDataNotice generatedAt={data?.generatedAt} />}

              {/* Assessments */}
              {viewMode === 'assessments' && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
                  <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-medium text-gray-900 flex items-center">
                      <ChartBarIcon className="w-5 h-5 text-violet-600 mr-2" />
                      Assessments
                    </h2>
                    <div className="flex items-center space-x-3">
                      {freshnessText && (
                        <span className="text-xs text-gray-500" title="Server-cached analytics to improve performance">
                          {freshnessText}
                        </span>
                      )}
                      <label className="text-sm text-gray-600">Time Period:</label>
                      <select
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                        className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-600 focus:border-violet-600 transition-colors"
                      >
                        <option value="day">Daily</option>
                        <option value="week">Weekly</option>
                        <option value="month">Monthly</option>
                      </select>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-shrink-0">
                            <div className="flex items-center justify-center w-10 h-10 bg-violet-500 rounded-lg">
                              <UsersIcon className="w-5 h-5 text-white" />
                            </div>
                          </div>
                          <div className="ml-4 text-right">
                            <p className="text-sm font-medium text-gray-700">Patients Added</p>
                            <p className="text-2xl font-bold text-gray-900">{analyticsSummary.totalAdded}</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-shrink-0">
                            <div className="flex items-center justify-center w-10 h-10 bg-violet-500 rounded-lg">
                              <CheckCircleIcon className="w-5 h-5 text-white" />
                            </div>
                          </div>
                          <div className="ml-4 text-right">
                            <p className="text-sm font-medium text-gray-700">Assessments Started</p>
                            <p className="text-2xl font-bold text-gray-900">{analyticsSummary.assessmentsStarted}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="px-5 pb-5">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <h3 className="text-sm font-medium text-gray-700 mb-3">Patients Added Over Time</h3>
                        <div className="relative h-64">
                          {chart.labels?.length ? (
                            <Bar
                              data={patientsAddedChartData}
                              options={CHART_OPTIONS_STATIC}
                            />
                          ) : (
                            <div className="flex items-center justify-center h-full text-gray-500">
                              <div className="text-center">
                                <ChartBarIcon className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                                <p className="text-sm">No data available</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <h3 className="text-sm font-medium text-gray-700 mb-3">Assessments Started Over Time</h3>
                        <div className="relative h-64">
                          {chart.labels?.length ? (
                            <Bar
                              data={assessmentsStartedChartData}
                              options={CHART_OPTIONS_STATIC}
                            />
                          ) : (
                            <div className="flex items-center justify-center h-full text-gray-500">
                              <div className="text-center">
                                <ChartBarIcon className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                                <p className="text-sm">No data available</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-shrink-0">
                            <div className="flex items-center justify-center w-10 h-10 bg-violet-500 rounded-lg">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                            </div>
                          </div>
                          <div className="ml-4 text-right">
                            <p className="text-sm font-medium text-gray-700">Assessment Rate</p>
                            <p className="text-2xl font-bold text-gray-900">{analyticsSummary.assessmentRate}</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-shrink-0">
                            <div className="flex items-center justify-center w-10 h-10 bg-violet-500 rounded-lg">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                          </div>
                          <div className="ml-4 text-right">
                            <p className="text-sm font-medium text-gray-700">Avg. Time to Assessment</p>
                            <p className="text-2xl font-bold text-gray-900">{analyticsSummary.avgTimeToStart}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Referrals — conditionally renders WLC or Phoenix layout based on
                  the referrals.type field from the backend. If type is undefined
                  (stale v3 cache during rollout), falls through to Phoenix since
                  only Phoenix had referrals pre-v4. */}
              {hasReferrals && viewMode === 'referrals' && data?.referrals?.type === 'wlc' && (
                <div className="space-y-6 mb-6">
                  {/* WLC Referral Analytics — header with period selector */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
                      <h2 className="text-lg font-medium text-gray-900 flex items-center">
                        <ChartBarIcon className="w-5 h-5 text-violet-600 mr-2" />
                        Referral Analytics
                      </h2>
                      <div className="flex items-center space-x-3">
                        {freshnessText && (
                          <span className="text-xs text-gray-500" title="Server-cached analytics to improve performance">
                            {freshnessText}
                          </span>
                        )}
                        <label className="text-sm text-gray-600">Source:</label>
                        <select
                          value={referralSourceFilter}
                          onChange={(e) => setReferralSourceFilter(e.target.value)}
                          className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-600 focus:border-violet-600 transition-colors"
                          title="Filter referral analytics by traffic source"
                        >
                          {REFERRAL_SOURCE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        <label className="text-sm text-gray-600">Time Period:</label>
                        <select
                          value={period}
                          onChange={(e) => setPeriod(e.target.value)}
                          className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-600 focus:border-violet-600 transition-colors"
                        >
                          <option value="day">Daily</option>
                          <option value="week">Weekly</option>
                          <option value="month">Monthly</option>
                        </select>
                        {/* Export: one workbook with Summary + By Day/Week/Month sheets,
                            scoped to the selected source, always all-time. */}
                        <div className="relative" ref={referralDownloadMenuRef}>
                          <button
                            onClick={() => setReferralDownloadMenuOpen((v) => !v)}
                            disabled={isDownloading || !referrals?.bySource}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-semibold rounded-lg text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            title="Download referrals and conversions as an Excel file"
                          >
                            {isDownloading ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Preparing...
                              </>
                            ) : (
                              <>
                                <DownloadIcon className="w-4 h-4 mr-1.5" />
                                Download
                                <svg className="w-4 h-4 ml-1 -mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                </svg>
                              </>
                            )}
                          </button>
                          {referralDownloadMenuOpen && (
                            <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                              <div className="py-1">
                                <button
                                  onClick={() => { setReferralDownloadMenuOpen(false); handleDownloadWlcReferrals(); }}
                                  disabled={!referrals?.bySource}
                                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:text-gray-400"
                                >
                                  Referrals &amp; Conversions (by day, week, month)
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="p-5">
                      {/* Row 0: at-a-glance split of referrals by traffic source
                          (all-time, independent of the source filter above). */}
                      {referralsBySource && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                          {(() => {
                            const allTotal = referralsBySource?.[REFERRAL_SOURCE_ALL]?.metrics?.totalReferrals || 0;
                            const cards = [
                              { key: REFERRAL_SOURCE_ALL, label: 'All Sources' },
                              ...REFERRAL_SOURCE_KEYS.map((k) => ({ key: k, label: referralSourceLabel(k) })),
                            ];
                            return cards.map(({ key, label }) => {
                              const count = referralsBySource?.[key]?.metrics?.totalReferrals || 0;
                              // Zero-guard the share so an empty dataset shows "--" not "NaN%".
                              const share = key === REFERRAL_SOURCE_ALL
                                ? null
                                : (allTotal > 0 ? `${Math.round((count / allTotal) * 100)}%` : '--');
                              return (
                                <div key={key} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                  <p className="text-sm font-medium text-gray-700">{label}</p>
                                  <p className="text-2xl font-bold text-gray-900 mt-1">{count}</p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {key === REFERRAL_SOURCE_ALL ? 'Total unique referrals' : `${share} of total referrals`}
                                  </p>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      )}

                      {/* Row 1: Three high-level metric cards */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex items-center justify-between">
                            <div className="flex-shrink-0">
                              <div className="flex items-center justify-center w-10 h-10 bg-violet-500 rounded-lg">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7h18M3 12h18M3 17h18" /></svg>
                              </div>
                            </div>
                            <div className="ml-4 text-right">
                              <p className="text-sm font-medium text-gray-700">Total Referrals</p>
                              <p className="text-2xl font-bold text-gray-900">{wlcReferralMetrics?.totalReferrals ?? 0}</p>
                              <p className="text-xs text-gray-500 mt-1">Unique submissions</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex items-center justify-between">
                            <div className="flex-shrink-0">
                              <div className="flex items-center justify-center w-10 h-10 bg-violet-500 rounded-lg">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                              </div>
                            </div>
                            <div className="ml-4 text-right">
                              <p className="text-sm font-medium text-gray-700">Conversion to Assessment</p>
                              <p className="text-2xl font-bold text-gray-900">
                                {wlcReferralMetrics?.conversionPct != null ? `${wlcReferralMetrics.conversionPct}%` : '--'}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {wlcReferralMetrics?.converted ?? 0} patient{(wlcReferralMetrics?.converted ?? 0) !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex items-center justify-between">
                            <div className="flex-shrink-0">
                              <div className="flex items-center justify-center w-10 h-10 bg-violet-500 rounded-lg">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                              </div>
                            </div>
                            <div className="ml-4 text-right">
                              <p className="text-sm font-medium text-gray-700">Avg. Time to Convert</p>
                              <p className="text-2xl font-bold text-gray-900">
                                {wlcReferralMetrics?.avgDaysToConvert != null ? `${wlcReferralMetrics.avgDaysToConvert} days` : '--'}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">Referral to assessment</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Row 2: Two side-by-side bar charts */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <h3 className="text-sm font-medium text-gray-700 mb-3">Unique Referrals Submitted</h3>
                          <div className="relative h-64">
                            {(wlcReferralsChart.labels || []).length ? (
                              <Bar
                                data={referralsWlcUniqueChartData}
                                options={CHART_OPTIONS_STATIC}
                              />
                            ) : (
                              <div className="flex items-center justify-center h-full text-gray-500">
                                <div className="text-center">
                                  <ChartBarIcon className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                                  <p className="text-sm">No data available</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <h3 className="text-sm font-medium text-gray-700 mb-3">Converted to Assessment</h3>
                          <div className="relative h-64">
                            {(wlcReferralsChart.labels || []).length && (wlcReferralsChart.convertedToAssessment || []).some(v => v > 0) ? (
                              <Bar
                                data={referralsWlcConvertedChartData}
                                options={CHART_OPTIONS_STATIC}
                              />
                            ) : (
                              <div className="flex items-center justify-center h-full text-gray-500">
                                <div className="text-center">
                                  <ChartBarIcon className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                                  <p className="text-sm">No conversion data available</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Row 3: Conversion Time Analysis — stats and distribution */}
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <h3 className="text-sm font-medium text-gray-700 mb-4">Conversion Time Analysis</h3>
                        {wlcReferralConversionStats ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Left: summary stats in a 2x2 grid */}
                            <div className="grid grid-cols-2 gap-3">
                              {[
                                { label: 'Mean', value: wlcReferralConversionStats.mean },
                                { label: 'Median', value: wlcReferralConversionStats.median },
                                { label: 'Min', value: wlcReferralConversionStats.min },
                                { label: 'Max', value: wlcReferralConversionStats.max },
                              ].map((stat) => (
                                <div key={stat.label} className="bg-white rounded-lg p-3 border border-gray-200 text-center">
                                  <p className="text-xs text-gray-500 uppercase tracking-wide">{stat.label}</p>
                                  <p className="text-xl font-bold text-gray-900 mt-1">{stat.value} <span className="text-sm font-normal text-gray-500">days</span></p>
                                </div>
                              ))}
                            </div>
                            {/* Right: distribution progress bars */}
                            <div className="space-y-3">
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
                                Distribution ({wlcReferralConversionStats.count} conversion{wlcReferralConversionStats.count !== 1 ? 's' : ''})
                              </p>
                              {(wlcReferralConversionStats.distribution || []).map((bucket) => (
                                <div key={bucket.label}>
                                  <div className="flex justify-between text-sm mb-1">
                                    <span className="text-gray-700">{bucket.label}</span>
                                    <span className="text-gray-900 font-medium">{bucket.count} ({bucket.pct}%)</span>
                                  </div>
                                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                                    <div
                                      className="bg-violet-500 h-2.5 rounded-full transition-all duration-300"
                                      style={{ width: `${Math.min(bucket.pct, 100)}%` }}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center py-8 text-gray-500">
                            <div className="text-center">
                              <svg className="w-8 h-8 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                              <p className="text-sm">No conversion data available yet</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Phoenix Referrals — shown when type is 'phoenix' or undefined (backward compat with stale v3 cache) */}
              {hasReferrals && viewMode === 'referrals' && data?.referrals?.type !== 'wlc' && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
                  <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-medium text-gray-900 flex items-center">
                      <ChartBarIcon className="w-5 h-5 text-violet-600 mr-2" />
                      Referrals
                    </h2>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-gray-600">Time Period:</label>
                      <select
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                        className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-600 focus:border-violet-600 transition-colors"
                      >
                        <option value="day">Daily</option>
                        <option value="week">Weekly</option>
                        <option value="month">Monthly</option>
                      </select>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-shrink-0">
                            <div className="flex items-center justify-center w-10 h-10 bg-violet-500 rounded-lg">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7h18M3 12h18M3 17h18" /></svg>
                            </div>
                          </div>
                          <div className="ml-4 text-right">
                            <p className="text-sm font-medium text-gray-700">Total Referrals</p>
                            <p className="text-2xl font-bold text-gray-900">{referrals.metrics?.totalReferrals || 0}</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-shrink-0">
                            <div className="flex items-center justify-center w-10 h-10 bg-violet-500 rounded-lg">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                            </div>
                          </div>
                          <div className="ml-4 text-right">
                            <p className="text-sm font-medium text-gray-700">Converted to Assessment</p>
                            <p className="text-2xl font-bold text-gray-900">{referrals.metrics?.converted || 0}</p>
                            {referrals.metrics?.totalReferrals > 0 && (
                              <p className="text-xs text-gray-500 mt-1">
                                {Math.round(((referrals.metrics?.converted || 0) / referrals.metrics.totalReferrals) * 100)}% of Total Referrals
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-shrink-0">
                            <div className="flex items-center justify-center w-10 h-10 bg-violet-500 rounded-lg">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                            </div>
                          </div>
                          <div className="ml-4 text-right">
                            <p className="text-sm font-medium text-gray-700">BUPA</p>
                            <p className="text-2xl font-bold text-gray-900">{referrals.metrics?.bupa || 0}</p>
                            {referrals.metrics?.totalReferrals > 0 && (
                              <p className="text-xs text-gray-500 mt-1">
                                {Math.round(((referrals.metrics?.bupa || 0) / referrals.metrics.totalReferrals) * 100)}% of Total Referrals
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-shrink-0">
                            <div className="flex items-center justify-center w-10 h-10 bg-violet-500 rounded-lg">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 1.79-3 4s1.343 4 3 4 3-1.79 3-4-1.343-4-3-4z" /></svg>
                            </div>
                          </div>
                          <div className="ml-4 text-right">
                            <p className="text-sm font-medium text-gray-700">Self Funding</p>
                            <p className="text-2xl font-bold text-gray-900">{referrals.metrics?.selfFunding || 0}</p>
                            {referrals.metrics?.totalReferrals > 0 && (
                              <p className="text-xs text-gray-500 mt-1">
                                {Math.round(((referrals.metrics?.selfFunding || 0) / referrals.metrics.totalReferrals) * 100)}% of Total Referrals
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-shrink-0">
                            <div className="flex items-center justify-center w-10 h-10 bg-violet-500 rounded-lg">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            </div>
                          </div>
                          <div className="ml-4 text-right">
                            <p className="text-sm font-medium text-gray-700">Declined</p>
                            <p className="text-2xl font-bold text-gray-900">{referrals.metrics?.declined || 0}</p>
                            {referrals.metrics?.totalReferrals > 0 && (
                              <p className="text-xs text-gray-500 mt-1">
                                {Math.round(((referrals.metrics?.declined || 0) / referrals.metrics.totalReferrals) * 100)}% of Total Referrals
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-shrink-0">
                            <div className="flex items-center justify-center w-10 h-10 bg-violet-500 rounded-lg">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            </div>
                          </div>
                          <div className="ml-4 text-right">
                            <p className="text-sm font-medium text-gray-700">Threshold Not Met</p>
                            <p className="text-2xl font-bold text-gray-900">{referrals.metrics?.thresholdNotMet || 0}</p>
                            {referrals.metrics?.totalReferrals > 0 && (
                              <p className="text-xs text-gray-500 mt-1">
                                {Math.round(((referrals.metrics?.thresholdNotMet || 0) / referrals.metrics.totalReferrals) * 100)}% of Total Referrals
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="px-1 pb-1">
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <h3 className="text-sm font-medium text-gray-700 mb-3">Referrals Over Time</h3>
                        <div className="relative h-64">
                          {(referralsChart.labels || []).length ? (
                            <Bar
                              data={referralsPhoenixChartData}
                              options={CHART_OPTIONS_STATIC}
                            />
                          ) : (
                            <div className="flex items-center justify-center h-full text-gray-500">
                              <div className="text-center">
                                <ChartBarIcon className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                                <p className="text-sm">No data available</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Forms / Patient Portal */}
              {viewMode === 'assessments' && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-medium text-gray-900 flex items-center">
                      <UsersIcon className="w-5 h-5 text-violet-600 mr-2" />
                      Patient Portal
                    </h2>
                  </div>
                  <div className="p-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-shrink-0">
                            <div className="flex items-center justify-center w-10 h-10 bg-violet-500 rounded-lg">
                              <CheckCircleIcon className="w-5 h-5 text-white" />
                            </div>
                          </div>
                          <div className="ml-4 text-right">
                            <p className="text-sm font-medium text-gray-700">Avg. Form Completion</p>
                            <p className="text-2xl font-bold text-gray-900">{forms.avgCompletion}%</p>
                            {forms.startedPatientsCount > 0 && (
                              <p className="text-xs text-gray-500 mt-1">
                                of {forms.startedPatientsCount} patients who started forms
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div
                        className="bg-gray-50 rounded-lg p-4 border border-gray-200 cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
                        onClick={openFeedbackModal}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-shrink-0">
                            <div className="flex items-center justify-center w-10 h-10 bg-violet-500 rounded-lg">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                              </svg>
                            </div>
                          </div>
                          <div className="ml-4 text-right">
                            <p className="text-sm font-medium text-gray-700">Avg. Patient Experience Rating</p>
                            <p className="text-2xl font-bold text-gray-900">{forms.patientExperience}</p>
                            <p className="text-xs font-semibold text-violet-600 mt-1">Click to view details</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-shrink-0">
                            <div className="flex items-center justify-center w-10 h-10 bg-violet-500 rounded-lg">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                          </div>
                          <div className="ml-4 text-right">
                            <p className="text-sm font-medium text-gray-700">Avg. Time per Form</p>
                            <p className="text-2xl font-bold text-gray-900">{forms.avgTimePerForm}</p>
                            {forms.completedAtLeastOneFormCount > 0 && (
                              <p className="text-xs text-gray-500 mt-1">of {forms.completedAtLeastOneFormCount} patients who completed at least one form</p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-shrink-0">
                            <div className="flex items-center justify-center w-10 h-10 bg-violet-500 rounded-lg">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                          </div>
                          <div className="ml-4 text-right">
                            <p className="text-sm font-medium text-gray-700">Avg. Time to Complete All Forms</p>
                            <p className="text-2xl font-bold text-gray-900">{forms.avgTimeAllForms}</p>
                            {forms.completedAllFormsCount > 0 && (
                              <p className="text-xs text-gray-500 mt-1">of {forms.completedAllFormsCount} patients who completed all forms</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Assessment Outcomes — diagnostic decision breakdown with
                  demographic cross-tabs. Only rendered when at least one
                  patient has a completed diagnostic decision. */}
              {viewMode === 'assessments' && hasAnyOutcomes && (
                <div className="mt-5 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-medium text-gray-900 flex items-center">
                      <ClipboardCheckIcon className="w-5 h-5 text-violet-600 mr-2" />
                      Assessment Outcomes
                    </h2>
                    <div className="flex items-center gap-3">
                      {/* Contract Type filter — independent of the Demographics one. */}
                      {showAssessmentTypeFilter && (
                        <div className="flex items-center gap-2">
                          <label htmlFor="outcomes-assessment-type-filter" className="text-sm font-medium text-gray-700">
                            Contract Type:
                          </label>
                          <select
                            id="outcomes-assessment-type-filter"
                            value={outcomesTypeFilter}
                            onChange={(e) => setOutcomesTypeFilter(e.target.value)}
                            className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-colors"
                          >
                            <option value="all">All</option>
                            {assessmentTypeOptions.map((type) => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div className="relative" ref={outcomesDownloadMenuRef}>
                        <button
                          onClick={() => setOutcomesDownloadMenuOpen((v) => !v)}
                          disabled={isDownloading || !outcomesHasData}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-semibold rounded-lg text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Download assessment outcomes data as Excel file"
                        >
                          {isDownloading ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Preparing...
                            </>
                          ) : (
                            <>
                              <DownloadIcon className="w-4 h-4 mr-1.5" />
                              Download
                              <svg className="w-4 h-4 ml-1 -mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                              </svg>
                            </>
                          )}
                        </button>
                        {outcomesDownloadMenuOpen && (
                          <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                            <div className="py-1">
                              <button
                                onClick={() => { setOutcomesDownloadMenuOpen(false); handleDownloadOutcomesAllTime(); }}
                                disabled={!outcomesByAssessmentType}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:text-gray-400"
                              >
                                All-time Data
                              </button>
                              <button
                                onClick={() => { setOutcomesDownloadMenuOpen(false); handleDownloadOutcomesLast6Months(); }}
                                disabled={!outcomesByAssessmentType?.all?.last6Months?.months?.length}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:text-gray-400"
                              >
                                Last 6 Months Data
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="p-5">
                    {!outcomesHasData ? (
                      <div className="text-center text-gray-500 py-8">
                        <ClipboardCheckIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                        <p>No assessment outcomes data available for the selected contract.</p>
                      </div>
                    ) : (
                    <>
                    {/* Row 1 — Three metric cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-shrink-0">
                            <div className="flex items-center justify-center w-10 h-10 bg-violet-500 rounded-lg">
                              <ClipboardCheckIcon className="w-5 h-5 text-white" />
                            </div>
                          </div>
                          <div className="ml-4 text-right">
                            <p className="text-sm font-medium text-gray-700">Diagnosis Decisions</p>
                            <p className="text-2xl font-bold text-gray-900">{outcomes.totalDecided}</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-shrink-0">
                            <div className="flex items-center justify-center w-10 h-10 bg-green-600 rounded-lg">
                              <CheckCircleIcon className="w-5 h-5 text-white" />
                            </div>
                          </div>
                          <div className="ml-4 text-right">
                            <p className="text-sm font-medium text-gray-700">Positive</p>
                            <p className="text-2xl font-bold text-green-700">{outcomes.positivePct != null ? `${outcomes.positivePct}%` : '-'}</p>
                            <p className="text-xs text-gray-500 mt-1">{outcomes.positive} patient{outcomes.positive !== 1 ? 's' : ''}</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-shrink-0">
                            <div className="flex items-center justify-center w-10 h-10 bg-red-500 rounded-lg">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            </div>
                          </div>
                          <div className="ml-4 text-right">
                            <p className="text-sm font-medium text-gray-700">Negative</p>
                            <p className="text-2xl font-bold text-red-600">{outcomes.negativePct != null ? `${outcomes.negativePct}%` : '-'}</p>
                            <p className="text-xs text-gray-500 mt-1">{outcomes.negative} patient{outcomes.negative !== 1 ? 's' : ''}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Row 2 — Fully met breakdown: Presentation & Severity */}
                    {outcomes.fullyMet > 0 && (
                      <div className="mb-6">
                        <p className="text-xs text-gray-500 mb-3 text-left">Of {outcomes.fullyMet} diagnoses where ADHD is "Fully met"</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Presentation subtypes */}
                          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <h3 className="text-sm font-medium text-gray-900 mb-3">Presentation Subtypes</h3>
                            {outcomes.presentation && outcomes.presentation.length > 0 ? (
                              <div className="space-y-2">
                                {outcomes.presentation.map((item) => (
                                  <div key={item.label}>
                                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                                      <span>{item.label}</span>
                                      <span>{item.count} ({item.pct}%)</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                      <div className="bg-violet-500 rounded-full h-2" style={{ width: `${item.pct}%` }} />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-400">No data</p>
                            )}
                          </div>
                          {/* Severity */}
                          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <h3 className="text-sm font-medium text-gray-900 mb-3">Severity</h3>
                            {outcomes.severity && outcomes.severity.length > 0 ? (
                              <div className="space-y-2">
                                {outcomes.severity.map((item) => (
                                  <div key={item.label}>
                                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                                      <span>{item.label}</span>
                                      <span>{item.count} ({item.pct}%)</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                      <div className="bg-amber-500 rounded-full h-2" style={{ width: `${item.pct}%` }} />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-400">No data</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Row 3 — Demographic breakdown tables (2-col grid) */}
                    <p className="text-xs text-gray-500 mb-3 text-left">Of all {outcomes.totalDecided} patients with an ADHD diagnosis decision</p>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {[
                        { title: 'By Age Group', rows: outcomes.byAgeGroup },
                        { title: 'By Gender Identity', rows: outcomes.byGender },
                        { title: 'By Ethnicity', rows: outcomes.byEthnicity },
                      ].map(({ title, rows }) => (
                        <div key={title} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <h3 className="text-sm font-medium text-gray-900 mb-3">{title}</h3>
                          {rows && rows.length > 0 ? (
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-xs text-gray-500 uppercase border-b border-gray-200">
                                  <th className="text-left py-2 pr-2">{title.replace('By ', '')}</th>
                                  <th className="text-right py-2 px-2">Total</th>
                                  <th className="text-right py-2 px-2">Pos</th>
                                  <th className="text-right py-2 px-2">Neg</th>
                                  <th className="text-right py-2 px-2">Pos %</th>
                                  <th className="text-right py-2 pl-2">Neg %</th>
                                </tr>
                              </thead>
                              <tbody>
                                {rows.map((row, idx) => (
                                  <tr key={row.label} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-100'}>
                                    <td className="py-1.5 pr-2 text-left text-gray-700">{row.label}</td>
                                    <td className="py-1.5 px-2 text-right text-gray-700">{row.total}</td>
                                    <td className="py-1.5 px-2 text-right text-green-700">{row.positive}</td>
                                    <td className="py-1.5 px-2 text-right text-red-600">{row.negative}</td>
                                    <td className="py-1.5 px-2 text-right text-gray-700">{row.positivePct}%</td>
                                    <td className="py-1.5 pl-2 text-right text-gray-700">{row.negativePct}%</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <p className="text-sm text-gray-400">No data</p>
                          )}
                        </div>
                      ))}
                    </div>
                    </>
                    )}
                  </div>
                </div>
              )}

              {/* Demographics */}
              {viewMode === 'assessments' && (
                <div className="mt-5 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-medium text-gray-900 flex items-center">
                      <UsersIcon className="w-5 h-5 text-violet-600 mr-2" />
                      Demographics
                    </h2>
                    <div className="flex items-center gap-3">
                      {showAssessmentTypeFilter && (
                        <div className="flex items-center gap-2">
                          <label htmlFor="assessment-type-filter" className="text-sm font-medium text-gray-700">
                            Contract Type:
                          </label>
                          <select
                            id="assessment-type-filter"
                            value={demographicsTypeFilter}
                            onChange={(e) => setDemographicsTypeFilter(e.target.value)}
                            className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-colors"
                          >
                            <option value="all">All</option>
                            {assessmentTypeOptions.map((type) => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div className="relative" ref={downloadMenuRef}>
                        <button
                          onClick={() => setDownloadMenuOpen((v) => !v)}
                          disabled={isDownloading || !demographicsHasData}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-semibold rounded-lg text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Download demographics data as Excel file"
                        >
                          {isDownloading ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Preparing...
                            </>
                          ) : (
                            <>
                              <DownloadIcon className="w-4 h-4 mr-1.5" />
                              Download
                              <svg className="w-4 h-4 ml-1 -mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                              </svg>
                            </>
                          )}
                        </button>
                        {downloadMenuOpen && (
                          <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                            <div className="py-1">
                              <button
                                onClick={() => { setDownloadMenuOpen(false); handleDownloadDemographicsAllTime(); }}
                                disabled={!demographicsHasData}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:text-gray-400"
                              >
                                All-time Data
                              </button>
                              <button
                                onClick={() => { setDownloadMenuOpen(false); handleDownloadDemographicsLast6Months(); }}
                                disabled={!demographicsLast6 || (demographicsLast6.months || []).length === 0}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:text-gray-400"
                              >
                                Last 6 Months Data
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="p-5">
                    {!demographicsHasData ? (
                      <div className="text-center text-gray-500 py-8">
                        <UsersIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                        <p>
                          {showAssessmentTypeFilter
                            ? 'No demographics data available for the selected contract.'
                            : 'No demographics data available.'}
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {demographicsDimensions
                          .map(([key, dim]) => {
                            const sortedResponses = sortedDemographicEntries(dim);
                            return (
                              <div
                                key={key}
                                className="bg-gray-50 rounded-lg p-4 border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
                                onClick={() => openDemographicsModal(key, dim)}
                              >
                                <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                                  {dim.display_name}
                                  <svg className="w-3 h-3 text-gray-400 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </h3>
                                <div className="text-xs text-gray-600 mb-2">{dim.total_responses} responses</div>
                                <div className="space-y-1">
                                  {sortedResponses.slice(0, 5).map(([response, count]) => {
                                    const pct = ((count / dim.total_responses) * 100).toFixed(1);
                                    const isSeenUnanswered = response === 'Seen but unanswered';
                                    const isNotYetSeen = response === 'Not yet seen';
                                    const isSpecial = isSeenUnanswered || isNotYetSeen;
                                    return (
                                      <div key={response}>
                                        <div className="flex justify-between items-center text-xs">
                                          <span className={`${isSpecial ? 'text-gray-500 italic' : 'text-gray-700'} truncate flex-1 mr-2 text-left`} title={response}>{response}</span>
                                          <div className="flex items-center space-x-1">
                                            <span className="text-gray-600">{count}</span>
                                            <span className="text-gray-500">({pct}%)</span>
                                          </div>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-1">
                                          <div
                                            className={`${isNotYetSeen ? 'bg-gray-300' : isSeenUnanswered ? 'bg-orange-400' : 'bg-violet-500'} h-1 rounded-full`}
                                            style={{ width: `${pct}%` }}
                                          ></div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                  {sortedResponses.length > 5 && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      ... and {sortedResponses.length - 5} more
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Feedback Modal */}
      {feedbackModalOpen && (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" onClick={() => setFeedbackModalOpen(false)}></div>
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-violet-500 rounded-lg">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                  </div>
                  <div><h3 className="text-lg font-semibold text-gray-900">Patient Experience Feedback</h3></div>
                </div>
                <button onClick={() => setFeedbackModalOpen(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                <div className="mb-6">
                  {feedback?.taskRatingCounts && Object.values(feedback.taskRatingCounts).reduce((a,b)=>a+b,0) > 0 && (
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">
                        <span className="text-gray-900">Task Rating</span>
                        <span className="text-sm font-normal text-gray-600 ml-2">
                          (Avg. Rating {feedback.avgTaskRating ?? '-'} out of 5 based on {Object.values(feedback.taskRatingCounts).reduce((a,b)=>a+b,0)} responses)
                        </span>
                      </h4>
                      <p className="text-sm text-gray-600 mb-4">"The Task: How did you find completing the questionnaires?"</p>
                      <div className="grid grid-cols-5 gap-3">
                        {[1,2,3,4,5].map(r => {
                          const count = feedback.taskRatingCounts[r] || 0;
                          const total = Object.values(feedback.taskRatingCounts).reduce((a,b)=>a+b,0) || 0;
                          const pct = total > 0 ? Math.round((count/total)*100) : 0;
                          return (
                            <div key={r} className="text-center p-3 bg-white border border-gray-200 rounded-lg">
                              <div className="text-2xl mb-2">{getEmojiForRating(r)}</div>
                              <div className="text-sm font-semibold text-gray-700">{count}</div>
                              <div className="text-xs text-gray-500">{pct}%</div>
                              <div className="text-xs text-gray-600 mt-1">{getTaskRatingLabel(r)}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {feedback?.platformRatingCounts && Object.values(feedback.platformRatingCounts).reduce((a,b)=>a+b,0) > 0 && (
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">
                        <span className="text-gray-900">Platform Rating</span>
                        <span className="text-sm font-normal text-gray-600 ml-2">
                          (Avg. Rating {feedback.avgPlatformRating ?? '-'} out of 5 based on {Object.values(feedback.platformRatingCounts).reduce((a,b)=>a+b,0)} responses)
                        </span>
                      </h4>
                      <p className="text-sm text-gray-600 mb-4">"The Platform: How was your experience using this platform?"</p>
                      <div className="grid grid-cols-5 gap-3">
                        {[1,2,3,4,5].map(r => {
                          const count = feedback.platformRatingCounts[r] || 0;
                          const total = Object.values(feedback.platformRatingCounts).reduce((a,b)=>a+b,0) || 0;
                          const pct = total > 0 ? Math.round((count/total)*100) : 0;
                          return (
                            <div key={r} className="text-center p-3 bg-white border border-gray-200 rounded-lg">
                              <div className="text-2xl mb-2">{getEmojiForRating(r)}</div>
                              <div className="text-sm font-semibold text-gray-700">{count}</div>
                              <div className="text-xs text-gray-500">{pct}%</div>
                              <div className="text-xs text-gray-600 mt-1">{getPlatformRatingLabel(r)}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  {Array.isArray(feedback?.list) && feedback.list.length > 0 ? (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">
                        Written Feedback ({sortedFeedbackList.length} responses)
                      </h4>
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {/* Key uses patientId + submittedAt to guarantee uniqueness even if
                            the same patient submitted feedback multiple times. Index-based
                            keys cause React reconciliation bugs when the list is re-sorted. */}
                        {sortedFeedbackList.map((f) => (
                          <div key={f.patientId ? `${f.patientId}_${f.submittedAt || ''}` : `${f.patientName || 'anon'}_${f.submittedAt || ''}`} className="bg-white border border-gray-200 rounded-lg p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center space-x-3">
                                {f.taskRating ? (
                                  <>
                                    <span className="text-lg">{getEmojiForRating(f.taskRating)}</span>
                                    <span className="text-sm font-medium text-gray-700">Task: {f.taskRating}/5</span>
                                  </>
                                ) : null}
                                {f.platformRating ? (
                                  <>
                                    <span className="text-lg">{getEmojiForRating(f.platformRating)}</span>
                                    <span className="text-sm font-medium text-gray-700">Platform: {f.platformRating}/5</span>
                                  </>
                                ) : null}
                                {!f.taskRating && !f.platformRating && f.legacyRating ? (
                                  <>
                                    <span className="text-lg">{getEmojiForRating(f.legacyRating)}</span>
                                    <span className="text-sm font-medium text-gray-700">Rating: {f.legacyRating}/5</span>
                                  </>
                                ) : null}
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-medium text-gray-700">
                                  {(f.referenceNumber && String(f.referenceNumber).trim())
                                    ? String(f.referenceNumber).trim()
                                    : (f.patientName || 'Unknown Patient')}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">{formatFeedbackDate(f.submittedAt)}</div>
                              </div>
                            </div>
                            <div className="text-sm text-gray-800 leading-relaxed">
                              "{f.feedback}"
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-gray-400 text-lg mb-2">📝</div>
                      <p className="text-gray-600">No written feedback available</p>
                      <p className="text-sm text-gray-500 mt-1">Patients provided ratings but no additional comments</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Demographics Detail Modal — safe-access pattern:
          selectedDemographic.data may be malformed or partially missing if the
          backend payload changes shape. We derive safe references up front so
          downstream JSX never throws on undefined property access. */}
      {demographicsModalOpen && selectedDemographic && (() => {
        const demoData = selectedDemographic?.data || {};
        const responses = demoData.responses || {};
        const totalResponses = demoData.total_responses || 0;
        return (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" onClick={closeDemographicsModal}></div>
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {demoData.display_name}
                </h3>
                <button onClick={closeDemographicsModal} className="text-gray-400 hover:text-gray-600 transition-colors">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                <div className="mb-4">
                  <div className="text-sm text-gray-600 mb-4">
                    Total responses: <span className="font-medium text-gray-900">{totalResponses}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  {Object.entries(responses)
                    .sort((a, b) => {
                      const getOrder = (resp) => {
                        if (resp === 'Not yet seen') return 3;
                        if (resp === 'Seen but unanswered') return 2;
                        return 1;
                      };
                      const oa = getOrder(a[0]);
                      const ob = getOrder(b[0]);
                      if (oa !== ob) return oa - ob;
                      return b[1] - a[1];
                    })
                    .map(([response, count]) => {
                      const pct = ((count / (totalResponses || 1)) * 100).toFixed(1);
                      const isSeenUnanswered = response === 'Seen but unanswered';
                      const isNotYetSeen = response === 'Not yet seen';
                      const isSpecial = isSeenUnanswered || isNotYetSeen;
                      return (
                        <div key={response} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex justify-between items-center mb-2">
                            <span className={`font-medium text-left ${isSpecial ? 'text-gray-500 italic' : 'text-gray-900'}`}>
                              {response}
                            </span>
                            <div className="flex items-center space-x-2">
                              <span className="text-lg font-semibold text-gray-900">{count}</span>
                              <span className="text-sm text-gray-500">({pct}%)</span>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`${isNotYetSeen ? 'bg-gray-300' : isSeenUnanswered ? 'bg-orange-400' : 'bg-violet-500'} h-2 rounded-full transition-all duration-300`}
                              style={{ width: `${pct}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

    </Layout>
  );
}

export default ProviderAnalytics;


